import { ChangeDetectionStrategy, Component, inject, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { Icon } from '../icon/icon';
import { Logo } from '../logo/logo';
import { StatusToolbar } from '../status-toolbar/status-toolbar';
import { TranslatedTitleStrategy } from '../../../core/i18n/translated-title.strategy';
import { PortalService } from '../../../core/portal.service';

/**
 * Topbar — the light, full-width header band (design system PageHeader). Left
 * to right: logo in the sidebar column, the current page title (from the route
 * title key, same source as the document title), a flexible gap, then the
 * status cluster (notifications, date/time, weather, search). The portal
 * identity (tenant switcher / admin capsule) lives at the top of the sidebar.
 * In the admin portal the bar carries an amber accent border to mark the
 * elevated context.
 */
@Component({
  selector: 'app-topbar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, Icon, Logo, StatusToolbar, TranslocoDirective],
  template: `
    <header
      *transloco="let t"
      class="flex h-16 shrink-0 items-center bg-page text-ink-1 md:h-24"
      [style.boxShadow]="inAdminPortal() ? 'inset 0 -3px 0 var(--admin-accent)' : null"
    >
      <!-- Phone shell: hamburger (opens the nav drawer) + compact logo mark.
           The full logo zone below md would eat most of the viewport. -->
      <button
        type="button"
        class="ml-2 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-control text-ink-1 hover:bg-sunken md:hidden"
        [attr.aria-label]="t('topbar.openMenu')"
        (click)="menuClick.emit()"
      >
        <app-icon name="icon-hamburger" [size]="24" />
      </button>
      <a
        [routerLink]="['/']"
        class="inline-flex h-full shrink-0 items-center px-2 text-ink-1 no-underline md:hidden"
      >
        <app-logo variant="mark" [height]="28" />
      </a>

      <!-- Logo zone spans the sidebar column (240px md–lg, 256px lg–2xl,
           296px from 2xl) so it lines up with the navbar beneath it; the
           logo is centred within that column. -->
      <a
        [routerLink]="['/']"
        class="hidden h-full w-60 shrink-0 items-center justify-center text-ink-1 no-underline md:inline-flex lg:w-64 2xl:w-[296px]"
      >
        <app-logo variant="full" [height]="36" />
      </a>

      <!-- Everything else lives in the region above the main content; px-4
           matches the main area's 16px gutter so the title lines up with the
           content card's left edge. -->
      <div class="flex min-w-0 flex-1 items-center gap-3.5 px-4">
        <!-- Page title (DS PageHeader): display face, muted ink, aligned with
             the content column's left edge. -->
        @if (pageTitleKey(); as titleKey) {
          <!-- Halfway between ink-2 and ink-1: darker than secondary text but
               still visibly muted next to true headings; flips with the theme. -->
          <h1
            class="min-w-0 truncate font-display text-h1 font-semibold"
            style="color: color-mix(in srgb, var(--ink-1) 50%, var(--ink-2))"
          >
            {{ t(titleKey) }}
          </h1>
        }

        <div class="flex-1"></div>

        <app-status-toolbar class="shrink-0" />
      </div>
    </header>
  `,
})
export class Topbar {
  private readonly portalService = inject(PortalService);

  protected readonly inAdminPortal = this.portalService.inAdminPortal;

  /** Route title key rendered as the in-bar page heading. */
  protected readonly pageTitleKey = inject(TranslatedTitleStrategy).pageTitleKey;

  /** Hamburger tap (phone shell only) — the app shell opens the nav drawer. */
  readonly menuClick = output<void>();
}
